import { atom, useAtom } from 'jotai';
import {
  Domaine,
  Item,
  Matiere,
  Periode,
  Programmation,
} from '@teetsh/app/src/types/programmations';

type ProgrammationHistory = { [key: string]: Array<ActionHistory> };

interface ICreateDomaine {
  action: 'CREATE_DOMAINE';
  payload: Domaine;
}

interface IDeleteDomaine {
  action: 'DELETE_DOMAINE';
  payload: Domaine;
}

interface IDeleteItem {
  action: 'DELETE_ITEM';
  payload: { item: Item; matiereId: string };
}

interface ICreateMatiere {
  action: 'CREATE_MATIERE';
  payload: Matiere;
}

interface IDeleteMatiere {
  action: 'DELETE_MATIERE';
  payload: Matiere;
}

interface IUpdateMatiere {
  action: 'UPDATE_MATIERE';
  payload: {
    oldMatiere: Matiere;
    newMatiere: Matiere;
  };
}

interface ICreatePeriode {
  action: 'CREATE_PERIODE';
  payload: Periode;
}

interface ICreateItem {
  action: 'CREATE_ITEM';
  payload: { item: Item; matiereId: string };
}

interface IDeletePeriode {
  action: 'DELETE_PERIODE';
  payload: Periode;
}

interface IUpdatePeriode {
  action: 'UPDATE_PERIODE';
  payload: {
    oldPeriode: Periode;
    newPeriode: Periode;
  };
}

interface IUpdateDomaines {
  action: 'UPDATE_DOMAINES';
  payload: {
    oldDomaines: Domaine[];
    newDomaines: Domaine[];
  };
}

interface IUpdateItem {
  action: 'UPDATE_ITEMS';
  payload: {
    oldItems: Item[];
    newItems: Item[];
    matiereId: string;
  };
}

interface IReorderMatieres {
  action: 'REORDER_MATIERES';
  payload: {
    oldMatieresIds: string[];
    newMatieresIds: string[];
  };
}

interface IReorderPeriodes {
  action: 'REORDER_PERIODES';
  payload: {
    oldPeriodeIds: string[];
    newPeriodeIds: string[];
  };
}

interface IReorderDomaines {
  action: 'REORDER_DOMAINES';
  payload: {
    matiereId: string;
    oldDomainesIds: string[];
    newDomainesIds: string[];
  };
}

interface IUpdateProgrammation {
  action: 'UPDATE_PROGRAMMATION';
  payload: {
    oldProgrammation: Programmation;
    newProgrammation: Programmation;
  };
}

export type ActionHistory =
  | ICreateMatiere
  | ICreatePeriode
  | ICreateDomaine
  | ICreateItem
  | IDeleteMatiere
  | IDeletePeriode
  | IDeleteDomaine
  | IDeleteItem
  | IUpdateMatiere
  | IUpdatePeriode
  | IUpdateDomaines
  | IUpdateItem
  | IReorderMatieres
  | IReorderDomaines
  | IReorderPeriodes
  | IUpdateProgrammation;

const futureProgrammationAtom = atom<ProgrammationHistory>({});
const pastProgrammationAtom = atom<ProgrammationHistory>({});

export const resetFutureAtom = atom(null, (get, set, programmationId: string) =>
  set(futureProgrammationAtom, {
    ...get(futureProgrammationAtom),
    [programmationId]: [],
  }),
);

const MAX_HISTORY_SIZE = 10;

export const addToPastAtom = atom(
  null,
  (
    get,
    set,
    {
      programmationId,
      actionHistory,
    }: { programmationId: string; actionHistory: ActionHistory },
  ) => {
    const past = get(pastProgrammationAtom);

    const progPast = past[programmationId] || [];
    const newPast = progPast.slice(MAX_HISTORY_SIZE * -1);
    return set(pastProgrammationAtom, {
      ...get(pastProgrammationAtom),
      [programmationId]: [...newPast, actionHistory],
    });
  },
);

export function useProgrammationHistory(programmationId: string) {
  const [past, setPast] = useAtom(pastProgrammationAtom);
  const [future, setFuture] = useAtom(futureProgrammationAtom);

  return {
    past: past[programmationId] || [],
    resetFuture: () => {
      setFuture({ ...future, [programmationId]: [] });
    },
    addToPast: (v: ActionHistory) => {
      const progPast = past[programmationId] || [];
      const newPast = progPast.slice(MAX_HISTORY_SIZE * -1);
      setPast({
        ...past,
        [programmationId]: [...newPast, v],
      });
    },
    isFutureEmpty: (future[programmationId] || []).length === 0,
    isPastEmpty: (past[programmationId] || []).length === 0,
    setPast: (v: Array<ActionHistory>) =>
      setPast({ ...past, [programmationId]: v }),
    setFuture: (v: Array<ActionHistory>) =>
      setFuture({ ...future, [programmationId]: v }),
    future: future[programmationId] || [],
  };
}
